<template>
    <v-list-item>
        <v-col>
            <PaymentMethodItemDetails :paymentMethod="paymentMethod">
                <template #actions>
                    <v-list-item-action
                    v-if="paymentMethodIdForPackage !== null"
                    class="ml-1"
                >
                    <v-chip
                        v-if="showCurrentChip"
                        color="success"
                        data-testid="current-chip"
                    >
                        {{ $t('message.current') }}
                    </v-chip>

                    <v-btn
                        v-else-if="showSelectButton"
                        color="primary"
                        @click="onUpdatePaymentMethod"
                        :disabled="statusObj.loading"
                        :loading="loadingSelectButton"
                        data-testid="select-button"
                    >
                        {{ $t('message.select') }}
                    </v-btn>

                    <v-btn
                        v-else-if="showCustomButton"
                        :color="buttonProps.color"
                        :outlined="buttonProps.outlined"
                        :small="buttonProps.small"
                        @click="onButtonClicked"
                        :disabled="statusObj.loading"
                        :loading="loadingSelectButton"
                        ref="customButton"
                    >
                        <span v-html="buttonProps.text"></span>
                    </v-btn>
                </v-list-item-action>

                <v-list-item-action>
                    <v-menu offset-y>
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn icon v-bind="attrs" v-on="on">
                                <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                        </template>
                        <v-list>
                            <v-list-item
                                v-if="enableDeleteCard"
                                @click="onDeletePaymentMethod"
                                data-testid="remove-button"
                            >
                                {{ $t('message.remove') }}
                            </v-list-item>
                            <v-list-item
                                v-else
                                :disabled="true"
                                data-testid="remove-button"
                            >
                                <span class="tw-text-textDisabled">
                                    {{ $t('message.remove') }}
                                </span>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </v-list-item-action>
                </template>
            </PaymentMethodItemDetails>

            <v-alert
                v-if="showRecurringPackagesInUseAlert"
                color="error"
                outlined
                text
                class="tw-text-sm tw-font-normal"
            >
                <span class="tw-font-bold">
                    <v-icon color="error">mdi-alert-octagon-outline</v-icon>
                    This payment method is used in
                    {{ recurringPackagesInUse }} recurring
                    {{ recurringPackagesInUse > 1 ? 'payments' : 'payment' }}
                </span>
                <v-list>
                    <v-list-item
                        v-for="pkg in paymentMethod.recurringPackagesInUse"
                        :key="pkg.id"
                    >
                        <v-list-item-avatar size="32" rounded="true">
                            <Avatar
                                :fullname="pkg.fullName"
                                :color="getColour(pkg.fullName)"
                            />
                        </v-list-item-avatar>

                        <v-list-item-content
                            class="tw-py-0 tw-text-xs tw-text-textSecondary"
                        >
                            <p class="tw-text-xs tw-font-medium tw-text-error">
                                {{ pkg.name }}
                            </p>

                            <span class="tw-text-xs tw-text-textSecondary">
                                {{ pkg.fullName }}'s Package
                            </span>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </v-alert>

            <v-divider class="mt-4"></v-divider>
        </v-col>
    </v-list-item>
</template>

<script>
import Avatar from 'vue-avatar-component'
import { getColour } from '@/util/profile'
import PaymentMethodItemDetails from '@/components/paymentmethods/PaymentMethodItemDetails.vue'
export default {
    components: {
        Avatar,
        PaymentMethodItemDetails,
    },
    props: {
        enableDeleteCard: {
            type: Boolean,
            required: true,
            default: false,
        },
        paymentMethod: {
            type: Object,
            required: true,
        },
        paymentMethodIdForPackage: {
            type: String,
            required: false,
            default: null,
        },
        paymentMethodIdToDelete: {
            type: String,
            required: false,
            default: null,
        },
        statusObj: {
            type: Object,
            required: false,
            default: () => ({
                loading: false,
                paymentMethodId: null,
            }),
        },
        isCharging: {
            type: Boolean,
            required: false,
        },
        buttonProps: {
            type: Object,
        },
    },
    emits: ['updatePaymentMethod', 'deletePaymentMethod'],
    computed: {
        loadingSelectButton() {
            return (
                this.statusObj.loading &&
                this.paymentMethod.id === this.statusObj.paymentMethodId
            )
        },
        showCurrentChip() {
            return (
                this.paymentMethodIdForPackage === this.paymentMethod.id &&
                !this.isCharging
            )
        },
        showSelectButton() {
            return (
                this.paymentMethodIdForPackage !== this.paymentMethod.id &&
                !this.isCharging
            )
        },
        showCustomButton() {
            return this.isCharging && this.buttonProps?.show
        },
        recurringPackagesInUse() {
            if (!this.paymentMethod.recurringPackagesInUse) {
                return 0
            }

            return this.paymentMethod.recurringPackagesInUse.length
        },
        showRecurringPackagesInUseAlert() {
            const isDeletingPaymentMethod =
                this.paymentMethodIdToDelete === this.paymentMethod.id

            return isDeletingPaymentMethod && this.recurringPackagesInUse > 0
        },
    },
    methods: {
        getColour,
        onUpdatePaymentMethod() {
            this.$emit('updatePaymentMethod', this.paymentMethod.id)
        },
        onDeletePaymentMethod() {
            this.$emit('deletePaymentMethod', this.paymentMethod.id)
        },
        onButtonClicked() {
            this.$emit('onButtonClicked', this.paymentMethod.id)
        },
    },
}
</script>
